import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Indicator,
  Input,
  Popover,
  PopoverButton,
  PopoverItem,
  SkeletonLine,
  StyledPopoverPanel,
  Tooltip,
} from '@crazyegginc/hatch';
import classNames from 'classnames';
import { useVirtualizer } from '@tanstack/react-virtual';

import { ReactComponent as InfoIcon } from '@crazyegginc/hatch/dist/images/icon-info-circle-outline.svg';
import { ReactComponent as CeBalloonIcon } from '@crazyegginc/hatch/dist/images/logo-ce-balloon.svg';
import { ReactComponent as GoogleAnalyticsIcon } from '@crazyegginc/hatch/dist/images/logo-google-analytics.svg';
import { ReactComponent as TickIcon } from '@crazyegginc/hatch/dist/images/icon-tick-basic.svg';

import { useMatches } from 'react-router-dom';
import { Pill } from '../Pill';
import { usePageParams } from '/src/hooks/usePageParams';
import { useAuthContext, useDebounce, useModal, useSite } from '/src/hooks';
import { AddSnapshotNewSiteModal } from '/src/features/snapshots/components/dashboard/modals/AddSnapshotNewSiteModal';
import { ConfirmRedirectModal } from '/src/features/google-analytics/modals/ConfirmRedirect';
import { ManageGaPropertiesModal } from '/src/features/google-analytics/modals/ManageGaProperties';
import { ALL, ALL_SITES } from '/src/features/_global/constants';
import { defaultRouteConfig } from '/src/Router';
import { AppLink } from '/src/features/_global/components/AppLink';

const FILTER_THRESHOLD = 8;
const SEARCH_DEBOUNCE_MS = 150;
const OPEN_PICKER_DELAY_MS = 150;
const MAX_PANEL_HEIGHT_PX = 600;
const INITIAL_VIRTUALIZER_RECT = { width: 0, height: MAX_PANEL_HEIGHT_PX };

const ESTIMATED_SITES_ROW_HEIGHT = 40; // matches sites estimateSize()
const ESTIMATED_GA_ROW_HEIGHT = 50; // requested max height for GA items
const ESTIMATED_SECTION_HEADING = 40; // SelectorHeading height (h-10)
const ESTIMATED_SECTION_PADDING = 16; // rough padding / incidental spacing
const ESTIMATED_GROUP_SEPARATOR = 16; // DashedSeparator with margins (rough)
const HALF_SECTION_TARGET = 250;

const SITES_SECTION_MIN_HEIGHT_CLASS = 'min-h-[80px]';
const GRID_ROWS_BOTH_BIG_CLASS = 'grid-rows-[minmax(80px,1fr)_1px_minmax(90px,1fr)]';
const GRID_ROWS_GA_BIG_CLASS = 'grid-rows-[minmax(80px,auto)_1px_minmax(90px,1fr)]';
const GRID_ROWS_SITES_BIG_CLASS = 'grid-rows-[minmax(80px,1fr)_1px_minmax(90px,auto)]';
const GRID_ROWS_BOTH_SMALL_CLASS = 'grid-rows-[minmax(80px,auto)_1px_minmax(90px,auto)]';
const GRID_ROWS_SITES_BIG_EMPTY_GA_CLASS = 'grid-rows-[minmax(80px,1fr)_1px_minmax(80px,auto)]';
const GRID_ROWS_BOTH_SMALL_EMPTY_GA_CLASS = 'grid-rows-[minmax(80px,auto)_1px_minmax(80px,auto)]';

const LARGE_LIST_OVERSCAN_THRESHOLD = 2000;
const SMALL_LIST_OVERSCAN = 16;
const LARGE_LIST_OVERSCAN = 8;

const getVirtualizerOverscan = (count) =>
  count > LARGE_LIST_OVERSCAN_THRESHOLD ? LARGE_LIST_OVERSCAN : SMALL_LIST_OVERSCAN;

export const SOURCE_TYPE = {
  SITE: 'SITE',
  GA_PROPERTY: 'GA_PROPERTY',
};

export function useSelectedSource({ allowAll = false, allowedSites = null } = {}) {
  const { allowAllSites, enableGoogleAnalytics } = usePageConfig();
  const { selectSite } = useSite({ enableAllSites: allowAll || allowAllSites, dontSelectSite: true });
  const { currentAccount, isSharing, sharedResource } = useAuthContext();

  const sites = useMemo(
    () =>
      isSharing ? [{ id: sharedResource?.siteId, name: sharedResource?.siteName }] : (currentAccount?.sites ?? []),
    [isSharing, currentAccount?.sites, sharedResource?.siteId, sharedResource?.siteName],
  );
  const gaProperties = useMemo(
    () => (isSharing ? [] : (currentAccount?.gaProperties ?? [])),
    [isSharing, currentAccount?.gaProperties],
  );

  const allSources = useMemo(
    () => [
      ...sites.map((s) => ({ ...s, type: SOURCE_TYPE.SITE })),
      ...gaProperties.map((g) => ({ ...g, type: SOURCE_TYPE.GA_PROPERTY })),
    ],
    [sites, gaProperties],
  );

  const allUserSelectableSources = useMemo(() => {
    return allSources.filter((s) => {
      if (!enableGoogleAnalytics) {
        return s.type !== SOURCE_TYPE.GA_PROPERTY;
      }
      // only return enabled Google Analytics properties for auto selection
      if (s.type === SOURCE_TYPE.GA_PROPERTY) {
        return s.enabledAt;
      }
      return true;
    });
  }, [allSources, enableGoogleAnalytics]);

  const allAutoSelectableSources = useMemo(() => {
    return allSources
      .filter((s) => {
        if (s.type === SOURCE_TYPE.SITE) {
          return s.active;
        }
        // only return enabled Google Analytics properties for auto selection
        if (s.type === SOURCE_TYPE.GA_PROPERTY) {
          return s.enabledAt;
        }
        return true;
      })
      .filter((s) => {
        if (!enableGoogleAnalytics) {
          return s.type !== SOURCE_TYPE.GA_PROPERTY;
        }
        return true;
      });
  }, [allSources, enableGoogleAnalytics]);

  const enabledSites = useMemo(() => {
    const sitesList = allowedSites ?? sites;
    return sitesList.map((s) => s.name);
  }, [allowedSites, sites]);

  const pageParamsConfig = useMemo(
    () => [
      {
        key: 'property',
        defaultValue: () => {
          if (enableGoogleAnalytics && localStorage.getItem('shell:property')) {
            return localStorage.getItem('shell:property');
          }
          return null;
        },
      },
      {
        key: 'site',
        defaultValue: () => {
          if (localStorage.getItem('shell:allsites') === 'true') {
            return ALL;
          }
          if (localStorage.getItem('shell:site') && enabledSites.includes(localStorage.getItem('shell:site'))) {
            return localStorage.getItem('shell:site');
          }
          if (allowAll || allowAllSites) {
            return ALL;
          }
          return null;
        },
      },
    ],
    [allowAll, allowAllSites, enabledSites, enableGoogleAnalytics],
  );

  const { status, params, setParams } = usePageParams(pageParamsConfig);

  const selectedSource = useMemo(() => {
    if (sites.length && params?.site === 'ALL') {
      return sites;
    }
    if (allUserSelectableSources.length === 1) {
      return allUserSelectableSources[0];
    }
    if (!params.property && !params.site) {
      return null;
    }

    const targetSource = allUserSelectableSources.find((s) => {
      if (params.property) {
        return String(s.id) === String(params.property) && s.type === SOURCE_TYPE.GA_PROPERTY;
      }
      return s.name.toLowerCase() === params.site.toLowerCase() && s.type === SOURCE_TYPE.SITE;
    });

    return targetSource || allUserSelectableSources[0];
  }, [allUserSelectableSources, sites, params.property, params.site]);

  const setSelectedSource = useCallback(
    (source) => {
      if (source === ALL_SITES) {
        localStorage.removeItem('shell:site');
        localStorage.removeItem('shell:property');
        localStorage.setItem('shell:allsites', 'true');
        selectSite('ALL');
        setParams({
          site: 'ALL',
          property: null,
        });
        return;
      }

      if (source?.type === SOURCE_TYPE.GA_PROPERTY) {
        localStorage.removeItem('shell:allsites');
        localStorage.setItem('shell:property', source.id);
        setParams({
          site: null,
          property: source.id,
        });
      } else {
        localStorage.removeItem('shell:allsites');
        localStorage.removeItem('shell:property');
        localStorage.setItem('shell:site', source.name);
        selectSite(source);
        setParams({
          property: null,
          site: source.name,
        });
      }
    },
    [setParams, selectSite],
  );

  const resetSourceSelection = useCallback(() => {
    localStorage.removeItem('shell:allsites');
    localStorage.removeItem('shell:property');
    localStorage.removeItem('shell:site');
    setSelectedSource(allAutoSelectableSources[0]);
  }, [setSelectedSource, allAutoSelectableSources]);

  useEffect(() => {
    if (selectedSource) return;
    if (!allAutoSelectableSources.length) return;

    if (!params.property && !params.site) {
      setSelectedSource(allAutoSelectableSources[0]);
    }
  }, [selectedSource, allAutoSelectableSources, params.property, params.site, setSelectedSource]);

  return {
    status,
    selectedSource,
    sourceId: params.source_id,
    sourceType: params.source_type?.toUpperCase() ?? null,
    setSelectedSource,
    resetSourceSelection,
  };
}

export function usePageConfig() {
  const matches = useMatches();

  // Take the last (deepest) match that has a pageConfig
  const routeConfig =
    matches
      .map((m) => m.handle?.pageConfig)
      .filter(Boolean)
      .at(-1) ?? {};

  return { ...defaultRouteConfig, ...routeConfig };
}

function SelectorHeading({ Icon, iconClass, title, info }) {
  return (
    <div className={classNames('w-[384px] h-10 px-4 py-2 flex items-center space-x-2 select-none')}>
      <Icon className={classNames('w-5 h-5 p-0.5', iconClass)} />
      <span className="text-header-4">{title}</span>
      {info ? (
        <Tooltip tooltipContent={info} placement="top">
          <InfoIcon className="fill-current text-cadet-blue-500 w-4 h-4" />
        </Tooltip>
      ) : null}
    </div>
  );
}

function DashedSeparator() {
  return <span className="w-[354px] self-center mx-auto my-2 border-t border-mystic-500 border-dashed" />;
}

const SelectorItem = memo(function SelectorItem({
  item,
  disabled = false,
  close,
  pill,
  title,
  subTitle,
  className: itemClassName,
  isSelected = false,
  onSelect,
}) {
  return (
    <PopoverItem
      className={classNames(
        'w-[384px] pl-12 pr-4 py-2 flex items-center bg-white relative justify-between',
        {
          'hover:bg-solitude-500 cursor-pointer': !disabled,
          'hover:bg-transparent cursor-auto': disabled,
        },
        itemClassName,
      )}
      onClick={() => {
        if (disabled) {
          close?.();
          return;
        }
        onSelect?.(item);
        close?.();
      }}
      aria-label={title || item.name}
    >
      <div className="flex w-full items-center">
        <div className="flex flex-col w-full min-w-0">
          <div className="flex w-full justify-between items-center min-w-0">
            <div className="flex items-center min-w-0">
              {isSelected ? (
                <TickIcon className="fill-current text-dodger-blue-500 absolute w-4 h-4 left-4 top-1/2 -translate-y-1/2" />
              ) : null}
              <span className={classNames('text-body-2 truncate', disabled && 'text-lynch-500')}>
                {title || item.name}
              </span>
            </div>
          </div>
          {subTitle ? <span className="text-body-4 mt-1 truncate">{subTitle}</span> : null}
        </div>
        <div className="flex-shrink-0">{pill}</div>
      </div>
    </PopoverItem>
  );
});

function estimateSitesSectionHeight(sitesCount) {
  return ESTIMATED_SECTION_HEADING + ESTIMATED_SECTION_PADDING + sitesCount * ESTIMATED_SITES_ROW_HEIGHT;
}

function estimateGaSectionHeight(groupedProperties) {
  const groups = Object.keys(groupedProperties).length;
  const items = Object.values(groupedProperties).reduce((acc, arr) => acc + arr.length, 0);
  const dashed = Math.max(0, groups - 1) * ESTIMATED_GROUP_SEPARATOR;

  return ESTIMATED_SECTION_HEADING + ESTIMATED_SECTION_PADDING + items * ESTIMATED_GA_ROW_HEIGHT + dashed;
}

function SourceSelectorPanel({
  open,
  searchable,
  placeholderText,
  search,
  setSearch,
  sites,
  filteredSources,
  sitesScrollRef,
  sitesVirtualizer,
  enabledGaProperties,
  groupedProperties,
  gaRows,
  gaScrollRef,
  gaVirtualizer,
  selectedSource,
  setSelectedSource,
  enableAllSites,
  enableGoogleAnalytics,
  gaProperties,
  addSnapshotOption,
  showEnableOtherSites,
  addSiteOption,
}) {
  useEffect(() => {
    if (!open) return;

    const hasMeasurableSitesHeight = (sitesScrollRef.current?.clientHeight ?? 0) > 0;
    const hasMeasurableGaHeight = (gaScrollRef.current?.clientHeight ?? 0) > 0;

    if (!hasMeasurableSitesHeight && !hasMeasurableGaHeight) return;

    const rafId = requestAnimationFrame(() => {
      sitesVirtualizer.measure();
      gaVirtualizer.measure();
    });

    return () => cancelAnimationFrame(rafId);
  }, [open, filteredSources.sites.length, gaRows.length, sitesScrollRef, gaScrollRef, sitesVirtualizer, gaVirtualizer]);

  const hasSites = sites.length > 0;
  const hasProperties = enabledGaProperties.length > 0;
  const splitSections = hasSites && hasProperties;
  const isSitesEmpty = filteredSources.sites.length === 0;
  const isGaEmpty = gaRows.length === 0;
  const siteVirtualItems = sitesVirtualizer.getVirtualItems();
  const shouldRenderSitesWithoutVirtualizer = !isSitesEmpty && siteVirtualItems.length === 0;
  const selectedSourceType = Array.isArray(selectedSource) ? null : selectedSource?.type;
  const selectedSourceId = Array.isArray(selectedSource) ? null : selectedSource?.id;

  const renderSiteItem = (s, close) => (
    <SelectorItem
      key={`site_item_${s.id}`}
      item={s}
      isSelected={selectedSourceType === SOURCE_TYPE.SITE && String(selectedSourceId) === String(s.id)}
      onSelect={setSelectedSource}
      pill={
        s.active ? (
          <Pill color="#0086E6" filled={true} className="h-6 font-semibold !text-white">
            Active
          </Pill>
        ) : (
          <Pill color="#E1E6EF" filled={true} className="h-6 font-semibold">
            Inactive
          </Pill>
        )
      }
      close={close}
    />
  );

  // "How much would each section like to take?"
  const estimatedSitesHeight = useMemo(
    () => estimateSitesSectionHeight(filteredSources.sites.length),
    [filteredSources.sites.length],
  );
  const estimatedGaHeight = useMemo(() => estimateGaSectionHeight(groupedProperties), [groupedProperties]);

  const halfTarget = HALF_SECTION_TARGET;

  const sitesBig = estimatedSitesHeight > halfTarget;
  const gaBig = estimatedGaHeight > halfTarget;

  // Choose grid rows:
  // - if both are big => 50/50 split (both scroll)
  // - else allow the small one to be auto
  const gridRowsClass = useMemo(() => {
    if (!splitSections) return null;

    if (isGaEmpty) {
      if (sitesBig) return GRID_ROWS_SITES_BIG_EMPTY_GA_CLASS;
      return GRID_ROWS_BOTH_SMALL_EMPTY_GA_CLASS;
    }

    if (sitesBig && gaBig) return GRID_ROWS_BOTH_BIG_CLASS;
    if (!sitesBig && gaBig) return GRID_ROWS_GA_BIG_CLASS;
    if (sitesBig && !gaBig) return GRID_ROWS_SITES_BIG_CLASS;

    return GRID_ROWS_BOTH_SMALL_CLASS;
  }, [splitSections, sitesBig, gaBig, isGaEmpty]);

  return (
    <StyledPopoverPanel
      data-testid="sitepicker-dropdown"
      style={{ maxHeight: MAX_PANEL_HEIGHT_PX }}
      className="bg-white !p-0 flex flex-col min-h-0 overflow-hidden overscroll-contain"
    >
      {({ close }) => (
        <>
          <div className="flex flex-col flex-1 min-h-0 overflow-hidden">
            <div className="border-r flex flex-1 min-h-0 flex-col border-mystic-500 w-[400px] py-2">
              {searchable ? (
                <div className="px-2 pb-2">
                  <Input
                    data-testid="SourceFilterInput"
                    placeholder={`Search for ${placeholderText}`}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </div>
              ) : null}

              <div
                className={classNames(
                  'flex-1 min-h-0 overflow-hidden',
                  splitSections ? classNames('grid', gridRowsClass) : 'flex flex-col',
                )}
              >
                {hasSites ? (
                  <section
                    ref={sitesScrollRef}
                    className={classNames(
                      'overflow-y-auto overflow-x-hidden overscroll-contain px-2',
                      'scrollbar scrollbar-track-white-lilac-500 scrollbar-thumb-cadet-blue-500 scrollbar-thumb-rounded',
                      SITES_SECTION_MIN_HEIGHT_CLASS,
                      !splitSections && 'flex-1',
                    )}
                  >
                    <SelectorHeading Icon={CeBalloonIcon} iconClass="fill-current text-lima-500" title="All Sites" />
                    {!isSitesEmpty ? (
                      shouldRenderSitesWithoutVirtualizer ? (
                        filteredSources.sites.map((s) => renderSiteItem(s, close))
                      ) : (
                        <div className="relative" style={{ height: sitesVirtualizer.getTotalSize() }}>
                          {siteVirtualItems.map((virtualRow) => {
                            const s = filteredSources.sites[virtualRow.index];

                            return (
                              <div
                                key={s.id}
                                className="absolute left-0 top-0 w-full"
                                style={{ transform: `translateY(${virtualRow.start}px)` }}
                              >
                                {renderSiteItem(s, close)}
                              </div>
                            );
                          })}
                        </div>
                      )
                    ) : (
                      <SelectorItem item={{ name: 'No sites match your criteria.' }} disabled={true} />
                    )}
                  </section>
                ) : null}

                {splitSections ? <span className="h-px bg-mystic-500 w-[354px] self-center mx-auto" /> : null}

                {hasProperties ? (
                  <section
                    ref={gaScrollRef}
                    className={classNames(
                      'overflow-y-auto overflow-x-hidden overscroll-contain px-2',
                      'scrollbar scrollbar-track-white-lilac-500 scrollbar-thumb-cadet-blue-500 scrollbar-thumb-rounded',
                      !splitSections && 'flex-1',
                    )}
                    style={{ minHeight: isGaEmpty ? 80 : 90 }}
                  >
                    <SelectorHeading
                      Icon={GoogleAnalyticsIcon}
                      title="Google Analytics Properties"
                      info={
                        <span className="text-left">
                          Data is being pulled from your
                          <br />
                          connected Google Analytics
                          <br />
                          account.
                        </span>
                      }
                    />

                    {!isGaEmpty ? (
                      <div className="relative" style={{ height: gaVirtualizer.getTotalSize() }}>
                        {gaVirtualizer.getVirtualItems().map((virtualRow) => {
                          const row = gaRows[virtualRow.index];

                          return (
                            <div
                              key={row.key}
                              className="absolute left-0 top-0 w-full"
                              style={{ transform: `translateY(${virtualRow.start}px)` }}
                            >
                              {row.type === 'separator' ? (
                                <DashedSeparator />
                              ) : (
                                <SelectorItem
                                  key={row.key}
                                  item={row.item}
                                  isSelected={
                                    selectedSourceType === SOURCE_TYPE.GA_PROPERTY &&
                                    String(selectedSourceId) === String(row.item?.id)
                                  }
                                  onSelect={setSelectedSource}
                                  close={close}
                                  subTitle={row.subTitle}
                                  className="h-[50px] max-h-[50px] py-1"
                                  pill={
                                    row.item?.lastError ? (
                                      <Tooltip
                                        tooltipContent={
                                          <div className="flex flex-col px-4 py-2 w-[240px] text-left leading-5 text-sm font-normal space-y-2.5">
                                            <span className="w-[215px]">
                                              We ran into a problem syncing data from Google Analytics into Crazy Egg.
                                            </span>
                                            <span className="w-[215px]">
                                              We’re on it and will update your Web Analytics dashboard as soon as data
                                              comes in. If the problem persists, contact Support for help.
                                            </span>
                                          </div>
                                        }
                                        placement="top"
                                      >
                                        <Pill color="#EF9906" filled={true} className="h-6 font-semibold !text-white">
                                          Error
                                        </Pill>
                                      </Tooltip>
                                    ) : null
                                  }
                                />
                              )}
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <SelectorItem item={{ name: 'No properties match your criteria.' }} disabled={true} />
                    )}
                  </section>
                ) : null}
              </div>
            </div>
          </div>

          <div className="flex flex-col">
            {enableAllSites ? (
              <ViewAllSitesButton
                onClick={() => {
                  setSelectedSource(ALL_SITES);
                  close?.();
                }}
              />
            ) : null}
            {enableGoogleAnalytics ? (
              <>{gaProperties.length > 0 ? <ConfigureGaAccounts close={close} /> : <ConnectGaButton close={close} />}</>
            ) : null}
            {addSnapshotOption ? <AddSnapshotsForAnotherSiteButton /> : null}
            {showEnableOtherSites ? <EnableOtherSitesButton /> : null}
            {addSiteOption ? <ManageSitesButton /> : null}
          </div>
        </>
      )}
    </StyledPopoverPanel>
  );
}

export function PickerActionLink({ to, disabled = false, children }) {
  return (
    <AppLink
      to={to}
      className={classNames(
        'w-full h-14 flex items-center text-dodger-blue-500 font-semibold text-sm border-t border-mystic-500 pl-6 pr-4',
        {
          'hover:bg-solitude-500': !disabled,
        },
      )}
    >
      {children}
    </AppLink>
  );
}

export function PickerActionButton({ onClick, disabled = false, children }) {
  return (
    <button
      onClick={onClick}
      className={classNames(
        'w-full h-14 flex items-center text-dodger-blue-500 font-semibold text-sm border-t border-mystic-500 pl-6 pr-4',
        {
          'hover:bg-solitude-500': !disabled,
        },
      )}
    >
      {children}
    </button>
  );
}

/* Common action buttons */

export function ManageSitesButton() {
  return <PickerActionLink to="/options/site-settings">Manage Sites</PickerActionLink>;
}

export function EnableOtherSitesButton() {
  return <PickerActionLink to="/options/site-settings">Enable Other Sites</PickerActionLink>;
}

export function ViewAllSitesButton({ onClick }) {
  return <PickerActionButton onClick={onClick}>View All Sites</PickerActionButton>;
}

function AddSnapshotsForAnotherSiteButton() {
  const modal = useModal();

  return (
    <PickerActionButton
      onClick={() => {
        modal.show(<AddSnapshotNewSiteModal />);
      }}
    >
      Add Heatmaps for Another Site
    </PickerActionButton>
  );
}

function ConfigureGaAccounts({ close }) {
  const modal = useModal();

  return (
    <PickerActionButton
      onClick={() => {
        modal.show(<ManageGaPropertiesModal />);
        close?.();
      }}
    >
      Google Analytics Settings
    </PickerActionButton>
  );
}

function ConnectGaButton({ close }) {
  const modal = useModal();

  return (
    <PickerActionButton
      onClick={() => {
        modal.show(<ConfirmRedirectModal />);
        close?.();
      }}
    >
      Connect Google Analytics
    </PickerActionButton>
  );
}

/*
 * Lets a user select a site (if any) or a GA property (if any)
 */
export function SourceSelector({
  sites = [],
  gaProperties = [],
  disabled = false,
  loading = false,
  enableGoogleAnalytics = false,
  enableAllSites = false,
  showEnableOtherSites = false,
  addSnapshotOption = false,
  addSiteOption = false,
}) {
  const sitesScrollRef = useRef(null);
  const gaScrollRef = useRef(null);

  const { selectedSource, setSelectedSource, resetSourceSelection } = useSelectedSource({
    allowAll: enableAllSites,
    allowedSites: sites,
  });

  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, SEARCH_DEBOUNCE_MS);
  const normalizedTerm = useMemo(() => debouncedSearch.trim().toLowerCase(), [debouncedSearch]);
  const hasMoreThanOne = sites.length + gaProperties.length > 1;
  const hasActions =
    enableGoogleAnalytics || enableAllSites || showEnableOtherSites || addSnapshotOption || addSiteOption;
  const buttonRef = useRef(null);

  useEffect(() => {
    const handleOpenPicker = () => {
      setTimeout(() => {
        buttonRef.current?.click();
      }, OPEN_PICKER_DELAY_MS);
    };

    const params = new URLSearchParams(window.location.search);
    if (params.get('open_picker') === 'true') {
      params.delete('open_picker');
      const newUrl = `${window.location.pathname}${params.toString() ? '?' + params.toString() : ''}${window.location.hash}`;
      window.history.replaceState({}, '', newUrl);
      handleOpenPicker();
    }

    window.addEventListener('openSourcePicker', handleOpenPicker);

    return () => {
      window.removeEventListener('openSourcePicker', handleOpenPicker);
    };
  }, []);

  const searchable = sites.length + gaProperties.length > FILTER_THRESHOLD;
  const placeholderText =
    gaProperties.length === 0
      ? 'a site'
      : sites.length === 0
        ? 'a Google Analytics property'
        : 'a site or a Google Analytics property';

  useEffect(() => {
    if (selectedSource?.type === SOURCE_TYPE.GA_PROPERTY && !enableGoogleAnalytics) {
      resetSourceSelection();
    }
  }, [selectedSource, enableGoogleAnalytics, resetSourceSelection]);

  const enabledGaProperties = useMemo(() => {
    return gaProperties.filter((p) => !!p.enabledAt);
  }, [gaProperties]);

  const sortedSites = useMemo(() => {
    return [...sites].sort((a, b) => {
      return Number(b.active) - Number(a.active) || a.name.localeCompare(b.name);
    });
  }, [sites]);

  const sitesSearchNames = useMemo(() => {
    return sortedSites.map((s) => s.name.toLowerCase());
  }, [sortedSites]);

  const gaSearchNames = useMemo(() => {
    return enabledGaProperties.map((p) => p.name.toLowerCase());
  }, [enabledGaProperties]);

  const filteredSources = useMemo(() => {
    const term = normalizedTerm;

    if (!term) {
      return {
        // group sites by status and order by name
        sites: sortedSites,
        gaProperties: enabledGaProperties,
      };
    }

    return {
      // group sites by status and order by name
      sites: sortedSites.filter((_, index) => sitesSearchNames[index].includes(term)),
      gaProperties: enabledGaProperties.filter((_, index) => gaSearchNames[index].includes(term)),
    };
  }, [normalizedTerm, sortedSites, enabledGaProperties, sitesSearchNames, gaSearchNames]);

  const sitesOverscan = getVirtualizerOverscan(filteredSources.sites.length);

  const sitesVirtualizer = useVirtualizer({
    count: filteredSources.sites.length,
    getScrollElement: () => sitesScrollRef.current,
    getItemKey: (index) => filteredSources.sites[index]?.id ?? index,
    estimateSize: () => ESTIMATED_SITES_ROW_HEIGHT,
    overscan: sitesOverscan,
    initialRect: INITIAL_VIRTUALIZER_RECT,
  });

  const groupedProperties = useMemo(() => {
    return Object.groupBy(filteredSources.gaProperties, ({ accountId }) => accountId);
  }, [filteredSources.gaProperties]);

  /**
   * Flatten grouped GA properties into a single virtualized list:
   * - separator rows between groups
   * - property rows with subTitle = account name (as before)
   */
  const gaRows = useMemo(() => {
    const entries = Object.entries(groupedProperties);

    const rows = [];
    entries.forEach(([accountId, properties], idx) => {
      if (idx > 0) {
        rows.push({
          type: 'separator',
          key: `ga_sep_${accountId}`,
        });
      }

      const accountName = properties?.[0]?.accountName;

      properties.forEach((g) => {
        rows.push({
          type: 'property',
          key: `ga_item_${g.id}`,
          item: g,
          subTitle: accountName,
        });
      });
    });

    return rows;
  }, [groupedProperties]);

  const gaVirtualizer = useVirtualizer({
    count: gaRows.length,
    getScrollElement: () => gaScrollRef.current,
    getItemKey: (index) => gaRows[index]?.key ?? index,
    estimateSize: (index) =>
      gaRows[index]?.type === 'separator' ? ESTIMATED_GROUP_SEPARATOR : ESTIMATED_GA_ROW_HEIGHT,
    overscan: getVirtualizerOverscan(gaRows.length),
    initialRect: INITIAL_VIRTUALIZER_RECT,
  });

  const displayText =
    Array.isArray(selectedSource) && selectedSource.length > 1
      ? 'All sites'
      : selectedSource
        ? selectedSource.name
        : null;

  return (
    <Popover>
      {({ open }) => (
        <>
          <PopoverButton
            ref={buttonRef}
            disabled={!selectedSource || disabled || (!hasMoreThanOne && !hasActions)}
            className="flex m-0 items-center text-header-2 select-none text-white focus-visible:outline-white space-x-2"
          >
            {!loading && displayText ? <span>{displayText}</span> : <SkeletonLine className="w-[250px] bg-white" />}
            {selectedSource && !disabled && (hasMoreThanOne || hasActions) ? <Indicator type="dropdown" /> : null}
          </PopoverButton>

          <SourceSelectorPanel
            open={open}
            searchable={searchable}
            placeholderText={placeholderText}
            search={search}
            setSearch={setSearch}
            sites={sites}
            filteredSources={filteredSources}
            sitesScrollRef={sitesScrollRef}
            sitesVirtualizer={sitesVirtualizer}
            enabledGaProperties={enabledGaProperties}
            groupedProperties={groupedProperties}
            gaRows={gaRows}
            gaScrollRef={gaScrollRef}
            gaVirtualizer={gaVirtualizer}
            selectedSource={selectedSource}
            setSelectedSource={setSelectedSource}
            enableAllSites={enableAllSites}
            enableGoogleAnalytics={enableGoogleAnalytics}
            gaProperties={gaProperties}
            addSnapshotOption={addSnapshotOption}
            showEnableOtherSites={showEnableOtherSites}
            addSiteOption={addSiteOption}
          />
        </>
      )}
    </Popover>
  );
}
